import { Controller } from "@hotwired/stimulus"

// The sub's bid page (no login), the prototype's SubBid flow. Every step is
// already on the page and this controller only shows the right blocks, so
// moving between them is instant: Yes / Can't bid → drop a file (presign →
// S3 PUT, with progress) or type the numbers → "Check your numbers" → Bid
// sent / Change my bid. The server is called only to save (optimistic: the
// page changes first and goes back if the save fails), plus the live
// countdown, questions and the addendum banners. In the builder's preview
// (step 3, previewValue) pop-ups and typing work, but nothing is ever sent,
// uploaded or saved: each of those says so instead.
export default class extends Controller {
  static targets = [
    "dueBox", "dueLabel", "dueCount", "banner",
    "step", "stepLine", "gcNote", "intent", "yesBtn", "upload", "dropzone", "working", "workingList", "bar", "ready", "readyList",
    "sent", "sentTitle", "sentTotal", "sentNote", "stale", "declined", "declinedNote",
    "checkModal", "checkSub", "line", "total", "cover", "uncovered", "inclusions", "exclusions", "modalFiles", "sendBtn",
    "declineModal", "reason", "declineNote", "declineBtn",
    "questions", "questionInput", "askBtn", "fileInput"
  ]
  static values = { urls: Object, state: Object, strings: Object, dueAt: String, readOnly: Boolean, multi: Boolean, seenCookie: String, path: String, preview: Boolean }

  connect() {
    this.st = { willBid: this.stateValue.will_bid, declined: !!this.stateValue.declined, version: this.stateValue.version || 0, stale: !!this.stateValue.stale }
    this._files = []      // uploaded, waiting to go with the next send: {cache_name, filename, content_type, byte_size}
    this._pending = []    // uploading now: {name, size}
    this._onKey = (e) => { if (e.key === "Escape") { this.closeCheck(); this.closeDecline() } }
    document.addEventListener("keydown", this._onKey)
    // A file dropped outside the drop zone never opens in the tab; with "Check your numbers" open it's added to the bid.
    this._onDocDrag = (e) => { if (this._hasFiles(e)) e.preventDefault() }
    this._onDocDrop = (e) => {
      if (!this._hasFiles(e) || e.target.closest?.("[data-precon-bid-target='dropzone']")) return
      e.preventDefault()
      if (this.hasCheckModalTarget && !this.checkModalTarget.hidden) this._upload(Array.from(e.dataTransfer.files || []))
    }
    document.addEventListener("dragover", this._onDocDrag)
    document.addEventListener("drop", this._onDocDrop)
    if (this.hasDueAtValue && this.dueAtValue && !this.readOnlyValue) this._tick = setInterval(() => this._countdown(), 1000)
    if (this.hasCoverTarget) this.coverChanged()
  }

  disconnect() {
    clearInterval(this._tick)
    document.removeEventListener("keydown", this._onKey)
    document.removeEventListener("dragover", this._onDocDrag)
    document.removeEventListener("drop", this._onDocDrop)
  }

  stop(e) { e.stopPropagation() }

  // The builder's preview: say nothing is sent, and stop.
  _inPreview() {
    if (!this.previewValue) return false
    this._toast(this.stringsValue.preview)
    return true
  }

  // ── The bid card ───────────────────────────────────────────────────────
  _render() {
    const { willBid, declined, version, stale } = this.st
    const sent = version > 0
    const busy = this._pending.length > 0
    const done = [true, willBid === true || sent, sent]
    const cur = declined ? -1 : done.indexOf(false)
    this.stepTargets.forEach((el, i) => { el.className = "tstep" + (done[i] ? " done" : (i === cur ? " cur" : "")) })
    this.stepLineTargets.forEach((el, i) => el.classList.toggle("on", !!done[i + 1]))
    const open = !sent && !declined && !this.readOnlyValue
    this._show(this.intentTarget, open)
    if (this.hasYesBtnTarget) {
      this.yesBtnTarget.classList.toggle("on", willBid === true)
      this.yesBtnTarget.textContent = willBid === true ? this.stringsValue.bidding : this.stringsValue.yes
    }
    this._show(this.uploadTarget, open && !busy && this._files.length === 0)
    this._show(this.workingTarget, busy)
    this._show(this.readyTarget, open && !busy && this._files.length > 0)
    this._show(this.sentTarget, sent && !declined)
    if (this.hasStaleTarget) this._show(this.staleTarget, sent && !declined && stale)
    this._show(this.declinedTarget, declined)
    if (this.hasSendBtnTarget) this.sendBtnTarget.textContent = sent ? this.stringsValue.send_update : this.stringsValue.send_first
  }

  _show(el, on) { if (el) el.hidden = !on }

  willBid() {
    if (this._inPreview() || this.st.willBid === true) return
    this._save(this.urlsValue.will_bid, { yes: "1" }, { willBid: true }, this.stringsValue.will_bid)
  }

  // Dropping a file or opening the form means they're bidding (as in the prototype); no toast.
  _ensureWillBid() {
    if (this.previewValue || this.st.willBid === true || this.st.version > 0 || this.readOnlyValue) return
    this._save(this.urlsValue.will_bid, { yes: "1" }, { willBid: true }, null)
  }

  openDecline() {
    this.reasonTargets.forEach(b => b.classList.remove("on"))
    this.declineNoteTarget.value = ""
    this.declineModalTarget.hidden = false
  }

  closeDecline() { if (this.hasDeclineModalTarget) this.declineModalTarget.hidden = true }

  pickReason(e) {
    const on = !e.currentTarget.classList.contains("on")
    this.reasonTargets.forEach(b => b.classList.remove("on"))
    e.currentTarget.classList.toggle("on", on)
  }

  sendDecline() {
    if (this._inPreview()) { this.closeDecline(); return }
    const reason = this.reasonTargets.find(b => b.classList.contains("on"))?.textContent.trim() || ""
    const note = this.declineNoteTarget.value.trim()
    const before = this.declinedNoteTarget.textContent
    this.declinedNoteTarget.textContent = [reason ? this._fmt(this.stringsValue.reason, { reason: reason.replace(/\.$/, "") }) : null, note || this.stringsValue.next].filter(Boolean).join(" ")
    this.closeDecline()
    this._save(this.urlsValue.decline, { reason, note }, { declined: true, willBid: false }, this.stringsValue.declined, () => { this.declinedNoteTarget.textContent = before })
  }

  undecline() { if (this._inPreview()) return; this._save(this.urlsValue.undecline, {}, { declined: false, willBid: null }, null) }

  // Change the page first, then save; put it back if the save fails.
  async _save(url, body, change, toast, onFail) {
    const before = { ...this.st }
    Object.assign(this.st, change)
    this._render()
    if (toast) this._toast(toast)
    try {
      await this._json(url, body)
    } catch (err) {
      this.st = before
      this._render()
      if (onFail) onFail()
      this._toast(err.message || this.stringsValue.error, "error")
    }
  }

  // ── Files: presign → S3 PUT (with progress) → sent with the bid ────────
  pick(e) { e?.preventDefault(); if (this._inPreview()) return; this.fileInputTarget.click() }
  picked(e) { this._upload(Array.from(e.target.files || [])); e.target.value = "" }
  dragOver(e) { e.preventDefault(); this.dropzoneTarget.classList.add("over") }
  dragLeave() { this.dropzoneTarget.classList.remove("over") }
  drop(e) { e.preventDefault(); this.dragLeave(); this._upload(Array.from(e.dataTransfer?.files || [])) }

  async _upload(files) {
    if (!files.length || this.readOnlyValue || this._inPreview()) return
    this._ensureWillBid()
    const items = files.map(f => ({ name: f.name, size: f.size, loaded: 0 }))
    this._pending.push(...items)
    this._setBar()
    this._renderFiles()
    this._render()
    try {
      const presign = await this._json(this.urlsValue.presign, { files: files.map(f => ({ filename: f.name, content_type: f.type || "application/octet-stream", byte_size: f.size })) })
      await Promise.all(presign.uploads.map((u, i) => this._put(u.upload_url, files[i], u.content_type || files[i].type || "application/octet-stream", (loaded) => { items[i].loaded = loaded; this._setBar() })))
      presign.uploads.forEach((u, i) => this._files.push({ cache_name: u.cache_name, filename: files[i].name, content_type: files[i].type, byte_size: files[i].size }))
      this._pending = this._pending.filter(p => !items.includes(p))
      this._renderFiles()
      this._render()
      if (this.checkModalTarget.hidden) this.openCheck()
      else this._setCheckSub()
    } catch (err) {
      this._pending = this._pending.filter(p => !items.includes(p))
      this._renderFiles()
      this._render()
      this._toast(err.message || this._fmt(this.stringsValue.upload_failed, {}), "error")
    }
  }

  _put(url, file, type, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open("PUT", url)
      xhr.setRequestHeader("Content-Type", type)
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded) }
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(this._fmt(this.stringsValue.upload_failed, { file: file.name }))))
      xhr.onerror = () => reject(new Error(this._fmt(this.stringsValue.upload_failed, { file: file.name })))
      xhr.send(file)
    })
  }

  _setBar() {
    const total = this._pending.reduce((a, p) => a + (p.size || 1), 0)
    const loaded = this._pending.reduce((a, p) => a + Math.min(p.loaded, p.size || 1), 0)
    this.barTarget.style.width = (total ? Math.max(6, Math.round((loaded / total) * 100)) : 0) + "%"
  }

  _renderFiles() {
    const chip = (name, size, extra) => {
      const el = document.createElement("div")
      el.className = "fchip"
      el.innerHTML = `${this._docIcon()}<span class="grow"></span>${size != null ? `<span class="muted">${this._size(size)}</span>` : ""}${extra}`
      el.querySelector(".grow").textContent = name
      return el
    }
    this.workingListTarget.replaceChildren(...this._pending.map(p => chip(p.name, p.size, "")))
    const done = (withBadge) => this._files.map(f => {
      const el = chip(f.filename, null, `${withBadge ? `<span class="badge b-green">${this._esc(this.stringsValue.attached)}</span>` : ""}<button class="xbtn" type="button" aria-label="${this._esc(this.stringsValue.remove)}">${this._xIcon()}</button>`)
      el.querySelector(".xbtn").addEventListener("click", (e) => { e.stopPropagation(); this._files = this._files.filter(x => x !== f); this._renderFiles(); this._render(); this._setCheckSub() })
      return el
    })
    this.readyListTarget.replaceChildren(...done(true))
    if (this.hasModalFilesTarget) {
      const busy = this._pending.map(p => chip(p.name, p.size, `<span class="spin" style="width:14px;height:14px;border-width:2px"></span>`))
      this.modalFilesTarget.replaceChildren(...done(false), ...busy)
    }
  }

  // ── Check your numbers ────────────────────────────────────────────────
  typeIt() { this._ensureWillBid(); this.openCheck() }
  changeBid() { this.openCheck() }

  openCheck() {
    this._setCheckSub()
    this.recalc()
    this.checkModalTarget.hidden = false
  }

  closeCheck() { if (this.hasCheckModalTarget) this.checkModalTarget.hidden = true }

  _setCheckSub() {
    if (!this.hasCheckSubTarget) return
    const s = this.stringsValue
    if (this._files.length) {
      const names = this._files.length > 1 ? `${this._files[0].filename} ${this._fmt(s.more, { count: this._files.length - 1 })}` : this._files[0].filename
      this.checkSubTarget.textContent = this._fmt(s.check_file, { file: names })
    } else this.checkSubTarget.textContent = this.st.version > 0 ? s.check_change : s.check_typed
  }

  recalc() {
    const total = this.lineTargets.reduce((a, el) => a + this._num(el.value), 0)
    this.totalTarget.textContent = this._money(total)
  }

  coverChanged() {
    if (!this.hasUncoveredTarget) return
    const off = this.coverTargets.filter(cb => !cb.checked).length
    this.uncoveredTarget.hidden = off === 0
    this.uncoveredTarget.textContent = this._fmt(off === 1 ? this.stringsValue.uncovered_one : this.stringsValue.uncovered_other, { count: off })
  }

  async send() {
    const s = this.stringsValue
    if (this._inPreview()) return
    if (this._pending.length) return
    if (this.multiValue && this.lineTargets.some(el => !el.value.trim())) { this._toast(s.price_every, "error"); this.lineTargets.find(el => !el.value.trim())?.focus(); return }
    const total = this.lineTargets.reduce((a, el) => a + this._num(el.value), 0)
    if (!(total > 0)) { this._toast(s.amount_first, "error"); this.lineTargets[0]?.focus(); return }
    const lines = {}
    if (this.multiValue) this.lineTargets.forEach(el => { lines[el.dataset.costCodeId] = String(this._num(el.value)) })
    const covers = {}
    this.coverTargets.forEach(cb => { (covers[cb.dataset.costCodeId] ||= [])[parseInt(cb.dataset.index, 10)] = cb.checked ? "1" : "0" })
    const body = { amount: String(total), lines, covers, inclusions: this.inclusionsTarget.value, exclusions: this.exclusionsTarget.value, files: this._files }
    const btn = this.sendBtnTarget
    btn.disabled = true
    btn.textContent = s.sending
    try {
      const data = await this._json(this.urlsValue.submit, body)
      Object.assign(this.st, { version: data.version, willBid: true, declined: false, stale: false })
      this.sentTitleTarget.textContent = data.sent_title
      this.sentTotalTarget.textContent = data.total || this._money(total)
      this.sentNoteTarget.textContent = s.sent_today
      if (this.hasGcNoteTarget) this.gcNoteTarget.hidden = true
      this._files = []
      this._renderFiles()
      this.closeCheck()
      this._render()
      this._toast(data.version > 1 ? this._fmt(s.sent_v, { version: data.version }) : s.sent)
    } catch (err) {
      this._toast(err.message || s.error, "error")
    } finally {
      btn.disabled = false
      this._render()
    }
  }

  // ── Questions ──────────────────────────────────────────────────────────
  async ask(e) {
    e.preventDefault()
    if (this._inPreview()) return
    const input = this.questionInputTarget
    const body = input.value.trim()
    if (!body) { this._toast(this.stringsValue.type_q, "error"); input.focus(); return }
    this.askBtnTarget.disabled = true
    try {
      const data = await this._json(this.urlsValue.question, { body })
      this.questionsTarget.insertAdjacentHTML("beforeend", data.row)
      input.value = ""
      this._toast(this.stringsValue.asked)
    } catch (err) {
      this._toast(err.message || this.stringsValue.error, "error")
    } finally {
      this.askBtnTarget.disabled = false
    }
  }

  // ── Addenda: "I've seen it" hides the banner in this browser ──────────
  ackAddendum(e) {
    if (this._inPreview()) return
    const banner = e.currentTarget.closest("[data-addendum-id]")
    banner.hidden = true
    const name = this.seenCookieValue
    const seen = new Set((document.cookie.split("; ").find(c => c.startsWith(name + "="))?.split("=")[1] || "").split(",").filter(Boolean))
    seen.add(banner.dataset.addendumId)
    document.cookie = `${name}=${[...seen].join(",")}; path=${this.pathValue}; max-age=31536000; samesite=lax`
  }

  // ── Due box: a live countdown, like the prototype ─────────────────────
  _countdown() {
    if (!this.hasDueBoxTarget) return
    const s = this.stringsValue
    const ms = new Date(this.dueAtValue).getTime() - Date.now()
    const past = ms <= 0
    const sec = Math.floor(Math.abs(ms) / 1000)
    const dd = Math.floor(sec / 86400), hh = Math.floor((sec % 86400) / 3600), mi = Math.floor((sec % 3600) / 60)
    const p2 = (x) => (x < 10 ? "0" : "") + x
    const parts = past ? [dd ? this._fmt(s.d, { n: dd }) : null, this._fmt(s.h, { n: hh })] : [dd ? this._fmt(s.d, { n: dd }) : null, this._fmt(s.h, { n: p2(hh) }), this._fmt(s.m, { n: p2(mi) }), this._fmt(s.s, { n: p2(sec % 60) })]
    const time = parts.filter(Boolean).join(" ")
    this.dueCountTarget.textContent = past ? this._fmt(s.ago, { time }) : time
    this.dueLabelTarget.textContent = past ? s.due_closed : s.due_in
    const box = this.dueBoxTarget
    box.classList.toggle("closed", past)
    box.classList.toggle("urgent", !past && ms < 86400000)
    box.classList.toggle("soon", !past && ms >= 86400000 && ms < 172800000)
    if (past) clearInterval(this._tick)
  }

  // ── Helpers ────────────────────────────────────────────────────────────
  _toast(message, type = "notice") {
    this.element.querySelectorAll(".toast").forEach(t => t.remove())
    const el = document.createElement("div")
    el.className = "toast" + (type === "error" ? " err" : "")
    el.setAttribute("role", "status")
    el.innerHTML = type === "error" ? this._alertIcon() : this._checkIcon()
    const span = document.createElement("span")
    span.textContent = message
    el.appendChild(span)
    el.addEventListener("click", () => el.remove())
    this.element.querySelector(".pg").appendChild(el)
    setTimeout(() => el.remove(), 5000)
  }

  async _json(url, body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token }, body: JSON.stringify(body) })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || data.ok === false) throw new Error(data.error || this.stringsValue.error)
    return data
  }

  _hasFiles(e) { return Array.from(e.dataTransfer?.types || []).includes("Files") }
  _fmt(str, vars) { return String(str || "").replace(/%\{(\w+)\}/g, (_, k) => (vars[k] ?? "")) }
  _num(v) { const x = parseFloat(String(v ?? "").replace(/[^0-9.]/g, "")); return isFinite(x) ? x : 0 }
  _money(n) { return "$" + Math.round(n || 0).toLocaleString("en-US") }
  _size(b) { return b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB" }
  _esc(s) { const d = document.createElement("div"); d.textContent = s; return d.innerHTML }
  _svg(d, w = 1.5) { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"></path></svg>` }
  _docIcon() { return this._svg("M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z") }
  _xIcon() { return this._svg("M6 18 18 6M6 6l12 12", 1.8) }
  _checkIcon() { return this._svg("m4.5 12.75 6 6 9-13.5", 2.2) }
  _alertIcon() { return this._svg("M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z", 1.8) }
}
